import type { ReactNode } from "react";
import type { SortDir, SortState } from "../lib/useSortableColumn";
import { Button } from "./Button";

// The shared list toolbar (Session AQT, Phase 1 design system foundation): a search box plus a
// slot for page-specific filter controls, sitting above a sortable table. Deliberately a
// controlled/presentational component -- it takes `search`/`onSearchChange` as props rather than
// calling useListSearch itself, mirroring how SortableHeader takes `sort`/`onToggle` rather than
// calling useSortableColumn itself. Pair it with useListSearch (src/lib/useListSearch.ts) for the
// URL-bound state and filterRows for applying it to a row list.

export interface ListToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  searchLabel?: string;
  filters?: ReactNode;
  className?: string;
  // Session AUJ (design system Session 2, spec 3.7). All three optional; omit them and the toolbar
  // renders exactly as before.
  /** "12 of 40 quotes": shown only while a search or filter narrows the list; aria-live="polite". */
  resultCount?: { shown: number; total: number; noun: string };
  /** A sort <select> shown below md only: a card list has no table header to click. */
  sort?: {
    state: SortState;
    options: { key: string; label: string }[];
    onChange: (state: SortState) => void;
    /** Show the select at every width (a table with columns that have no header to click). */
    alwaysVisible?: boolean;
  };
  /** A "Clear" ghost button, shown while a search is typed or the list is narrowed. */
  onClear?: () => void;
}

const SORT_SELECT =
  "w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm text-ele-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ele-green";

export function ListToolbar({
  search,
  onSearchChange,
  searchPlaceholder = "Search...",
  searchLabel = "Search",
  filters,
  className = "",
  resultCount,
  sort,
  onClear,
}: ListToolbarProps) {
  const narrowed = resultCount != null && resultCount.shown < resultCount.total;
  const showClear = onClear != null && (search.trim() !== "" || narrowed);
  const sortValue = sort?.state.sortKey ? `${sort.state.sortKey}|${sort.state.sortDir}` : "";
  return (
    <div
      className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${className}`.trim()}
    >
      <input
        type="search"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={searchPlaceholder}
        aria-label={searchLabel}
        className="w-full min-w-0 rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm text-ele-strong placeholder:text-ele-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ele-green sm:max-w-xs"
      />
      {sort != null && (
        <select
          aria-label="Sort by"
          value={sortValue}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) return sort.onChange({ sortKey: null, sortDir: "asc" });
            const [key, dir] = v.split("|");
            sort.onChange({ sortKey: key, sortDir: dir as SortDir });
          }}
          className={`${SORT_SELECT}${sort.alwaysVisible ? " sm:max-w-[14rem]" : " md:hidden"}`}
        >
          <option value="">Default order</option>
          {sort.options.flatMap((o) => [
            <option key={`${o.key}|asc`} value={`${o.key}|asc`}>{`${o.label} (ascending)`}</option>,
            <option key={`${o.key}|desc`} value={`${o.key}|desc`}>{`${o.label} (descending)`}</option>,
          ])}
        </select>
      )}
      {(filters != null || resultCount != null || showClear) && (
        <div className="flex min-w-min max-w-full flex-wrap items-center gap-2 [&>a]:whitespace-nowrap [&>button]:whitespace-nowrap">
          {filters}
          {resultCount != null && (
            <span aria-live="polite" className="text-xs text-ele-body">
              {narrowed ? `${resultCount.shown} of ${resultCount.total} ${resultCount.noun}` : ""}
            </span>
          )}
          {showClear && (
            <Button variant="ghost" size="sm" onClick={onClear}>
              Clear
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
